import React, { FC, memo, useEffect, useState } from 'react';

import { TEXTS } from 'application/pages/texts';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { RouterUtils, useNavigate } from 'application/providers/RouterProvider';
import { POINTS_MENU_PATH } from 'application/consts';
import { useStreamRepositoryInjector } from 'application/providers/DIContainerProvider';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { PointAddressesList, ModalTitle, ModalDescription, SuccessActionButton } from './components';
import { BREAK_EDGE_VALUE } from './consts';
import useStreamBindSuccessModalViewModel from './useStreamBindSuccessModalViewModel';
import { useOnboardingContext } from '../useOnboardingContext';
import { useOnboardingActionContext } from '../useOnboardingActionContext';

import styles from './index.module.scss';

const StreamBindSuccessModal: FC<ModalProps[ModalTypes.STREAM_BIND_SUCCESS]> = ({
  close,
  selectedPoints: selectedPointsProps = [],
  successCallback,
  replaceModal,
}) => {
  const { currentPoint } = useOnboardingContext();
  const { resetState, clearCurrentPoint } = useOnboardingActionContext();
  const { clearStreamWithThematic } = useStreamRepositoryInjector();
  const navigate = useNavigate();

  useEffect(
    () => () => {
      resetState();
      clearStreamWithThematic();
    },
    []
  );

  const {
    selectedPoints,
    showWebPlayerButton,
    isAtLeastOnePointWithoutDevice,
    onShowWebPlayerClick,
    onShowPointsPage,
    onClose,
  } = useStreamBindSuccessModalViewModel({
    selectedPointsProps,
    currentPoint,
    redirectToPointsPage: () => {
      navigate(RouterUtils.generatePath(POINTS_MENU_PATH));
    },
    close,
    successCallback,
    replaceModal,
    clearCurrentPoint,
  });

  const [isAllPointsVisible, setIsAllPointsVisible] = useState<boolean>(false);

  const showExpandButton = selectedPoints.length > BREAK_EDGE_VALUE && !isAllPointsVisible;

  return (
    <ModalScreen
      title={
        <ModalTitle
          selectedPoints={selectedPoints}
          isAtLeastOnePointWithoutDevice={isAtLeastOnePointWithoutDevice}
        />
      }
      titlePlacement="left"
      containerClassName={styles.container}
      onClose={onClose}
      actionButtons={[
        <SuccessActionButton
          key="button_open_whatever_bind_success_modal"
          showWebPlayerButton={showWebPlayerButton}
          onShowWebPlayerClick={onShowWebPlayerClick}
          onShowPointsPage={onShowPointsPage}
        />,
        <Button
          key="button_cancel_bind_success_modal"
          elementId="button_cancel_bind_success_modal"
          type={ButtonType.SECONDARY}
          onClick={onClose}>
          {TEXTS.MODAL_BIND_SUCCESS_BUTTON_CANCEL_TEXT}
        </Button>,
      ]}>
      <ModalDescription
        selectedPoints={selectedPoints}
        isAtLeastOnePointWithoutDevice={isAtLeastOnePointWithoutDevice}
      />

      {selectedPoints.length > 0 && (
        <div className={styles.pointList}>
          <PointAddressesList
            selectedPoints={showExpandButton ? selectedPoints.slice(0, BREAK_EDGE_VALUE) : selectedPoints}
          />

          {showExpandButton && (
            <Button
              elementId="stream_bind_success_modal_button_show_all"
              className={styles.button}
              type={ButtonType.PLAIN}
              onClick={() => setIsAllPointsVisible(true)}>
              {TEXTS.MODAL_BIND_SUCCESS_MORE_ADDRESS(selectedPoints.length - BREAK_EDGE_VALUE)}
            </Button>
          )}
        </div>
      )}
    </ModalScreen>
  );
};

export default memo(StreamBindSuccessModal);
